import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import TextHeader from '../../components/textHeader/TextHeader';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import MissingPage from '../../components/shared/errorMessages/MissingPage';
import CMSConsumerCarouselBanner from '../../section/CMS/components/banner/CMSConsumerCarouselBanner';
import MailingListSignup from '../../components/shared/mailing/Mailing';
import CompanyGrid from '../../components/shared/grid/CompanyGrid';
import AccordionComponent from '../../components/shared/accordion/AccordionComponent';
import CareersAccordion from '../../components/shared/accordion/CareersAccordion';
import { getCategoryPageData } from '../../section/CMS/api/cmsApi';
import { CMSPageData, CareerOpening, FrequentQuestion } from '../../section/CMS/types/CMSTypes';
import { captureAnalytics } from '../../utilities/Analytics';

import { Box, Typography } from '@mui/material';
import AnnouncementIcon from '@mui/icons-material/Announcement';

const getFileServerUrl = (): string => {
  const currentDomain = window.location.hostname;
  if (currentDomain.includes('staging')) {
    return 'https://file-server.hertex-staging.net';
  }
  return 'https://file-server.hertex.co.za';
};

const constructImageUrl = (filename: string): string => {
  if (!filename) return '';
  const fileServerUrl = getFileServerUrl();
  const cleanUrl = fileServerUrl.replace('/api', '').replace(/\/$/, '');
  return `${cleanUrl}/media/large/${filename}`;
};

const transformUrl = (url: string): string => {
  if (!url) return '';
  return url.replace('https://trade.hertex.co.za/', 'https://hertex.co.za/');
};

const renderHTMLContent = (htmlString: string) => {
  if (!htmlString) return null;

  const sanitizedHTML = htmlString.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  return (
    <Typography
      component="div"
      sx={{
        '& ul': { paddingLeft: 2, margin: '8px 0' },
        '& li': { marginBottom: '4px' },
        '& p': { margin: '8px 0' },
        fontSize: 16,
        color: 'rgba(71, 84, 103, 1)',
        mb: 2,
        whiteSpace: 'pre-wrap'
      }}
      dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
    />
  );
};

const CareerPage = () => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const location = useLocation();

  const handleChange =
    (panel: string) =>
      (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
      };

  const {
    isError: cmsError,
    isLoading: cmsLoading,
    data: cmsResponse
  } = useQuery(
    ['cms-careers'],
    () => getCategoryPageData('careers', 'live', 'careers'),
    { staleTime: 600000 }
  );

  const cmsData: CMSPageData | null = cmsResponse?.cms_page || null;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    captureAnalytics('pageview', location.pathname, 'Careers');
  }, [location.pathname]);

  if (cmsLoading) return <LoadingScreen />;
  if (cmsError || !cmsData) return <MissingPage />;

  const careerOpenings: CareerOpening[] = cmsData.careers?.active || [];
  const frequentQuestions: FrequentQuestion[] = cmsData.frequent_questions?.active || [];

  return (
    <>
      {/* CMS Banner */}
      <CMSConsumerCarouselBanner
        bannerMediaOption={cmsData.banner_media_option || 'carousel'}
        bannerMediaCarousel={cmsData.banner_media_carousel || []}
        bannerMediaVideo={cmsData.banner_media_video || null}
        bannerMediaVideoLink={cmsData.banner_media_video_link || ''}
        bannerLinkCategoryId={cmsData.banner_link_category_id || null}
        bannerWidth={cmsData.banner_width || 2880}
        bannerHeight={cmsData.banner_height || 1200}
      />

      {/* Page Title and Description */}
      <TextHeader
        heading={cmsData.page_title || 'Join our team'}
        content={cmsData.description || ''}
        isCentered={false}
        width='70%'
        marginTop={10}
        marginBottom={7}
      />

      {/* Careers Accordion */}
      <Box sx={{ width: '70%', ml: '15%', mb: '5rem' }}>
        {careerOpenings.length > 0 ? (
          <CareersAccordion careersData={careerOpenings} renderHTMLContent={renderHTMLContent} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mb: 8 }}>
            <AnnouncementIcon fontSize={'large'} />
            <Typography>No current job openings available</Typography>
            <Typography>Please check back later</Typography>
          </Box>
        )}
      </Box>

      {/* POPI Act Section */}
      {cmsData.popi_act_display && (cmsData.popi_act_title || cmsData.popi_act_field) && (
        <Box sx={{ width: '70%', ml: '15%', mb: '5rem' }}>
          {cmsData.popi_act_title && (
            <Typography
              variant="h3"
              mb={3}
              fontWeight={500}
              textTransform="none"
              sx={{ fontSize: { xs: '32px', lg: '40px' } }}
            >
              {cmsData.popi_act_title}
            </Typography>
          )}
          {cmsData.popi_act_field && (
            <Typography
              fontSize={{ xs: '16px', lg: '20px' }}
              fontWeight={400}
              lineHeight="26px"
              sx={{ textTransform: 'none' }}
            >
              {cmsData.popi_act_field}
            </Typography>
          )}
        </Box>
      )}

      {/* Large Media Section */}
      {cmsData.large_media_carousel && cmsData.large_media_carousel.length > 0 && (
        <CMSConsumerCarouselBanner
          bannerMediaOption={cmsData.large_media_option || 'carousel'}
          bannerMediaCarousel={cmsData.large_media_carousel || []}
          bannerMediaVideo={null}
          bannerMediaVideoLink=""
          bannerLinkCategoryId={null}
          bannerWidth={2880}
          bannerHeight={1800}
        />
      )}

      {/* FAQ Heading */}
      <TextHeader
        heading='Frequently Asked Questions'
        isCentered={false}
        width='70%'
        marginTop={10}
        marginBottom={7}
      />

      {/* FAQ Accordion */}
      <Box sx={{ width: '70%', ml: '15%', mb: '5rem' }}>
        {frequentQuestions.map((faq, index) => (
          <AccordionComponent
            key={faq.id}
            title={faq.question_heading}
            panelNumber={index}
            expanded={expanded}
            handleChange={handleChange}
            displayFAQs={true}
            questions={[{ text_1: faq.questions_body, text_2: '' }]}
            isTop={'20px'}
          />
        ))}
      </Box>

      {/* Questions Section */}
      {cmsData.question_section_title && (
        <CompanyGrid
          marginTop={15}
          marginBottom={15}
          image={cmsData.question_section_media ? constructImageUrl(cmsData.question_section_media) : ''}
          hasImage={!!cmsData.question_section_media}
          isReverse={true}
          isContained={true}
          heading={cmsData.question_section_title}
          paragraph1={cmsData.question_section_description || ''}
          hasExternalLink={!!cmsData.question_section_web_link}
          externalLink={transformUrl(cmsData.question_section_web_link || '')}
          padTop={15}
          hasButton={!!cmsData.question_section_link_button_text}
          buttonTitle={cmsData.question_section_link_button_text || ''}
        />
      )}

      {/* Social Section */}
      {cmsData.socials_section_title && (
        <CompanyGrid
          marginTop={15}
          marginBottom={15}
          image={cmsData.socials_section_media ? constructImageUrl(cmsData.socials_section_media) : ''}
          hasImage={!!cmsData.socials_section_media}
          isReverse={false}
          isContained={true}
          heading={cmsData.socials_section_title}
          paragraph1={cmsData.socials_section_description || ''}
          padTop={15}
          hasButton={!!cmsData.socials_section_link_button_text}
          hasExternalLink={!!cmsData.socials_section_web_link}
          externalLink={transformUrl(cmsData.socials_section_web_link || '')}
          buttonTitle={cmsData.socials_section_link_button_text || ''}
        />
      )}

      <MailingListSignup isColor={true} marginBottom={5} />
    </>
  );
};

export default CareerPage;